import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import type { SavedOrder } from "@/hooks/useOrders";
import { buildRewardsData } from "@/hooks/useRewards";
import { useActivityLog } from "@/hooks/useActivityLog";

interface UserOrder extends SavedOrder {
  userPhone: string;
  pointsEarned?: number;
}

const STATUS_OPTIONS = [
  { value: "pending",   label: "قيد المعالجة", color: "text-amber-600",  bg: "bg-amber-50",   border: "border-amber-200",  icon: "ri-time-line" },
  { value: "confirmed", label: "تم التأكيد",   color: "text-green-600",  bg: "bg-green-50",   border: "border-green-200",  icon: "ri-checkbox-circle-line" },
  { value: "shipped",   label: "تم الشحن",     color: "text-indigo-600", bg: "bg-indigo-50",  border: "border-indigo-200", icon: "ri-send-plane-line" },
  { value: "delivered", label: "تم التوصيل",   color: "text-teal-600",   bg: "bg-teal-50",    border: "border-teal-200",   icon: "ri-truck-line" },
  { value: "cancelled", label: "ملغي",          color: "text-red-600",    bg: "bg-red-50",     border: "border-red-200",    icon: "ri-close-circle-line" },
];

interface OrdersManagerProps {
  onToast: (msg: string, type?: 'success' | 'error') => void;
  currentUserEmail?: string;
  currentUserName?: string;
}

// SMS notification via Edge Function
const sendSmsNotification = async (
  order: UserOrder,
  status: SavedOrder["status"]
): Promise<{ sent: boolean; setupRequired?: boolean; fallbackMsg?: string; fallbackPhone?: string }> => {
  try {
    const { data, error } = await supabase.functions.invoke("sms-notify", {
      body: {
        phone: order.phone,
        name: order.name,
        orderRef: order.orderRef,
        status,
        items: order.items,
        finalPrice: order.finalPrice,
      },
    });

    if (error) return { sent: false };

    if (data?.setup_required) {
      return {
        sent: false,
        setupRequired: true,
        fallbackMsg: data.fallback_message,
        fallbackPhone: data.fallback_phone,
      };
    }

    return { sent: !!data?.success };
  } catch {
    return { sent: false };
  }
};

interface WhatsAppResult {
  autoSent: boolean;
  apiConfigured: boolean;
  whatsappUrl?: string;
  adminWhatsAppUrl?: string;
}

// WhatsApp auto notification on status change
const sendWhatsAppStatusUpdate = async (
  order: UserOrder,
  status: SavedOrder["status"]
): Promise<WhatsAppResult> => {
  try {
    const { data, error } = await supabase.functions.invoke("whatsapp-notify", {
      body: {
        type: "status_update",
        orderRef: order.orderRef,
        phone: order.phone,
        name: order.name,
        city: order.city,
        status,
        items: order.items,
        finalPrice: order.finalPrice,
        paymentLabel: order.paymentLabel,
      },
    });

    if (error || !data?.success) return { autoSent: false, apiConfigured: false };
    return {
      autoSent: data.autoSent ?? false,
      apiConfigured: data.apiConfigured ?? false,
      whatsappUrl: data.whatsappUrl,
      adminWhatsAppUrl: data.adminWhatsAppUrl,
    };
  } catch {
    return { autoSent: false, apiConfigured: false };
  }
};

// Manual WhatsApp notification (opens browser)
const sendWhatsAppNotification = async (
  type: "admin" | "customer",
  order: UserOrder,
  newStatus?: SavedOrder["status"]
): Promise<boolean> => {
  try {
    const { data, error } = await supabase.functions.invoke("whatsapp-notify", {
      body: {
        type,
        orderRef: order.orderRef,
        phone: order.phone,
        name: order.name,
        city: order.city,
        status: newStatus || order.status,
        items: order.items,
        finalPrice: order.finalPrice,
        paymentLabel: order.paymentLabel,
      },
    });

    if (error || !data?.success) return false;
    window.open(data.whatsappUrl, "_blank", "noopener,noreferrer");
    return true;
  } catch {
    return false;
  }
};

// ── Date helpers ──
function startOfDayLocal(d: Date) { const x = new Date(d); x.setHours(0,0,0,0); return x; }
function startOfWeekLocal(d: Date) { const x = startOfDayLocal(d); x.setDate(x.getDate() - x.getDay()); return x; }
function startOfMonthLocal(d: Date) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function startOfLastMonthLocal(d: Date) { return new Date(d.getFullYear(), d.getMonth() - 1, 1); }

export default function OrdersManager({ onToast, currentUserEmail = '', currentUserName = '' }: OrdersManagerProps) {
  const navigate = useNavigate();
  const { logActivity } = useActivityLog();
  const [orders, setOrders] = useState<UserOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [filterPayment, setFilterPayment] = useState<string>("all");
  const [search, setSearch] = useState("");
  const [sendingNotification, setSendingNotification] = useState<string | null>(null);
  const [cancelConfirm, setCancelConfirm] = useState<{ order: UserOrder; reason: string } | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [priceMin, setPriceMin] = useState("");
  const [priceMax, setPriceMax] = useState("");
  const [smsResult, setSmsResult] = useState<{
    orderRef: string;
    sent: boolean;
    setupRequired?: boolean;
    fallbackMsg?: string;
    fallbackPhone?: string;
  } | null>(null);
  const [waResult, setWaResult] = useState<{
    orderRef: string;
    autoSent: boolean;
    apiConfigured: boolean;
    whatsappUrl?: string;
  } | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("orders")
      .select("*")
      .order("created_at", { ascending: false });

    if (!error && data) {
      const mapped: UserOrder[] = data.map((row) => ({
        orderRef: row.order_ref,
        date: row.created_at,
        name: row.name,
        phone: row.phone,
        city: row.city,
        address: row.address,
        notes: row.notes || "",
        paymentLabel: row.payment_label,
        items: row.items,
        totalPrice: row.total_price,
        discountCode: row.discount_code || "",
        discountPercent: row.discount_percent || 0,
        discountAmount: row.discount_amount || 0,
        finalPrice: row.final_price,
        status: row.status as SavedOrder["status"],
        customerId: row.customer_id ?? undefined,
        pointsEarned: row.points_earned ?? 0,
        userPhone: row.phone,
      }));
      setOrders(mapped);
    }
    setLoading(false);
  }, []);

  useEffect(() => { reload(); }, [reload]);

  /**
   * Restore stock when cancelling an order.
   * Stock is deducted at order placement, so we restore on ANY cancellation
   * (regardless of previous status), unless the order was already cancelled.
   */
  const restoreStock = async (order: UserOrder): Promise<void> => {
    try {
      if (order.status === "cancelled") return; // already cancelled — nothing to restore
      for (const item of order.items) {
        const productId = item.id;
        if (!productId) continue;

        const { data: productRow } = await supabase
          .from("products")
          .select("id, stock")
          .eq("id", productId)
          .maybeSingle();

        if (!productRow) continue;
        const currentStock = productRow.stock ?? 0;
        await supabase
          .from("products")
          .update({ stock: currentStock + (item.qty ?? 1) })
          .eq("id", productId);
      }
    } catch { /* silent */ }
  };

  const handleStatusChange = async (orderRef: string, status: SavedOrder["status"]) => {
    const order = orders.find((o) => o.orderRef === orderRef);
    if (!order) return;

    setSendingNotification(orderRef);
    setSmsResult(null);
    setWaResult(null);

    // Update status in database
    const { error } = await supabase
      .from("orders")
      .update({ status })
      .eq("order_ref", orderRef);

    if (!error) {
      reload();
      const label = STATUS_OPTIONS.find((s) => s.value === status)?.label || status;
      onToast(`تم تحديث حالة الطلب #${orderRef} إلى "${label}"`);

      // Log activity
      logActivity({
        actionType: 'order_status_change',
        entityType: 'order',
        entityId: orderRef,
        entityName: `طلب #${orderRef} — ${order.name}`,
        adminEmail: currentUserEmail,
        adminName: currentUserName || currentUserEmail.split('@')[0],
        details: {
          previousStatus: order.status,
          newStatus: status,
          newStatusLabel: label,
          customerName: order.name,
          customerPhone: order.phone,
          city: order.city,
          finalPrice: order.finalPrice,
        },
      });

      // Grant reward points ONLY when order is delivered
      if (status === "delivered" && order.customerId) {
        try {
          const { data: orderRow } = await supabase
            .from("orders")
            .select("id, points_earned")
            .eq("order_ref", orderRef)
            .maybeSingle();

          if (orderRow && (!orderRow.points_earned || orderRow.points_earned === 0)) {
            const earned = Math.floor(order.finalPrice / 10);
            if (earned > 0) {
              const { data: customer } = await supabase
                .from("customers")
                .select("points, total_points_earned")
                .eq("id", order.customerId)
                .maybeSingle();

              const currentPoints = customer?.points ?? 0;
              const currentTotal = customer?.total_points_earned ?? 0;
              const newPoints = currentPoints + earned;

              const oldRewards = buildRewardsData(currentPoints, currentTotal);
              const newRewards = buildRewardsData(newPoints, currentTotal + earned);
              const tierUpgraded = oldRewards.tier !== newRewards.tier;

              await supabase.from("customers").update({
                points: newPoints,
                total_points_earned: currentTotal + earned,
              }).eq("id", order.customerId);

              await supabase.from("orders").update({ points_earned: earned }).eq("id", orderRow.id);
              onToast(`تم استلام الطلب — مُنح ${earned} نقطة للعميل ${order.name} (${newRewards.tierLabel}) 🎉`);

              // Send WhatsApp points notification to customer
              try {
                const waPoints = await supabase.functions.invoke("whatsapp-notify", {
                  body: {
                    type: "points_earned",
                    phone: order.phone,
                    name: order.name,
                    orderRef: order.orderRef,
                    pointsEarned: earned,
                    totalPoints: newPoints,
                    tierLabel: newRewards.tierLabel,
                    nextTierPoints: newRewards.nextTierPoints,
                    nextTierLabel: newRewards.nextTierLabel,
                  },
                });
                if (waPoints.data?.autoSent) {
                  onToast(`تم إرسال إشعار واتساب بالنقاط للعميل ${order.name} ✓`);
                }
              } catch { /* silent */ }

              if (tierUpgraded) {
                const tierIcons: Record<string, string> = { silver: "🥈", gold: "🥇", platinum: "💎" };
                await supabase.functions.invoke("sms-notify", {
                  body: {
                    type: "tier_upgrade",
                    phone: order.phone,
                    name: order.name,
                    tierLabel: newRewards.tierLabel,
                    tierIcon: tierIcons[newRewards.tier] ?? "⭐",
                    points: newPoints,
                  },
                });
                onToast(`تم إرسال SMS ترقية المستوى للعميل ${order.name} — ${newRewards.tierLabel} 🎉`);
              }
            }
          }
        } catch { /* silent */ }
      }

      // ── Email notification (fire & forget) ──
      supabase.functions.invoke("send-order-email", {
        body: {
          orderRef: order.orderRef,
          phone: order.phone,
          status,
          items: order.items,
          finalPrice: order.finalPrice,
          paymentLabel: order.paymentLabel,
          city: order.city,
          name: order.name,
        },
      }).then((res) => {
        if (res.data?.sent) {
          onToast(`تم إرسال إشعار بريد إلكتروني للعميل ${order.name} ✉️`);
        }
      }).catch(() => { /* silent */ });

      // ── Auto WhatsApp notification ──
      const wa = await sendWhatsAppStatusUpdate(order, status);
      setWaResult({ orderRef, ...wa });

      if (wa.autoSent) {
        onToast(`تم إرسال إشعار واتساب تلقائياً للعميل ${order.name} ✓`);
      } else if (!wa.apiConfigured) {
        // API not configured — show fallback banner (no toast spam)
      } else {
        onToast(`تعذّر إرسال واتساب تلقائياً — جرّب الإرسال اليدوي`, "error");
      }

      // Send SMS notification automatically
      const sms = await sendSmsNotification(order, status);
      setSmsResult({ orderRef, ...sms });

      if (sms.sent) {
        onToast(`تم إرسال SMS للعميل ${order.name} ✓`);
      } else if (sms.setupRequired) {
        onToast(`يحتاج إعداد Twilio لإرسال SMS — راجع التعليمات`, "error");
      }
    }

    setSendingNotification(null);
  };

  const handleSendAdminNotification = async (order: UserOrder) => {
    setSendingNotification(order.orderRef);
    const sent = await sendWhatsAppNotification("admin", order);
    if (sent) {
      onToast("تم فتح واتساب لإرسال إشعار للإدارة");
    } else {
      onToast("فشل إرسال الإشعار، يرجى المحاولة مرة أخرى");
    }
    setSendingNotification(null);
  };

  const handleSendCustomerNotification = async (order: UserOrder) => {
    setSendingNotification(order.orderRef);
    const sent = await sendWhatsAppNotification("customer", order, order.status);
    if (sent) {
      onToast("تم فتح واتساب لإرسال إشعار للعميل");
    } else {
      onToast("فشل إرسال الإشعار، يرجى المحاولة مرة أخرى");
    }
    setSendingNotification(null);
  };

  /** Refund points when cancelling a delivered order */
  const refundPointsOnCancel = async (order: UserOrder): Promise<number> => {
    if (!order.customerId) return 0;
    try {
      // Get the actual points_earned stored on the order row
      const { data: orderRow } = await supabase
        .from("orders")
        .select("id, points_earned, status")
        .eq("order_ref", order.orderRef)
        .maybeSingle();

      const pointsToRefund = orderRow?.points_earned ?? 0;
      if (pointsToRefund <= 0) return 0;

      // Get current customer points
      const { data: customer } = await supabase
        .from("customers")
        .select("points, total_points_earned")
        .eq("id", order.customerId)
        .maybeSingle();

      const currentPoints = customer?.points ?? 0;
      const currentTotal = customer?.total_points_earned ?? 0;
      const newPoints = Math.max(0, currentPoints - pointsToRefund);
      const newTotal = Math.max(0, currentTotal - pointsToRefund);

      // Deduct points from customer
      await supabase
        .from("customers")
        .update({ points: newPoints, total_points_earned: newTotal })
        .eq("id", order.customerId);

      // Reset points_earned on order to 0
      if (orderRow?.id) {
        await supabase
          .from("orders")
          .update({ points_earned: 0 })
          .eq("id", orderRow.id);
      }

      return pointsToRefund;
    } catch {
      return 0;
    }
  };

  const handleCancelOrder = async () => {
    if (!cancelConfirm) return;
    const { order, reason } = cancelConfirm;
    setCancelling(true);

    const existingNotes: string = order.notes ?? "";
    const updatedNotes: string = existingNotes
      ? `${existingNotes}\n[إلغاء: ${reason}]`
      : `[إلغاء: ${reason}]`;

    const { error } = await supabase
      .from("orders")
      .update({ status: "cancelled", notes: updatedNotes })
      .eq("order_ref", order.orderRef);

    if (!error) {
      // Refund points if the order was delivered and had points earned
      const refunded = await refundPointsOnCancel(order);
      if (refunded > 0) {
        onToast(`تم استرجاع ${refunded} نقطة من العميل ${order.name} بسبب الإلغاء`);
      }

      // Restore stock if order was delivered
      await restoreStock(order);

      reload();
      onToast(`تم إلغاء الطلب #${order.orderRef} بنجاح`);

      // Log activity
      logActivity({
        actionType: 'order_cancel',
        entityType: 'order',
        entityId: order.orderRef,
        entityName: `طلب #${order.orderRef} — ${order.name}`,
        adminEmail: currentUserEmail,
        adminName: currentUserName || currentUserEmail.split('@')[0],
        details: {
          cancelReason: reason,
          previousStatus: order.status,
          customerName: order.name,
          customerPhone: order.phone,
          city: order.city,
          finalPrice: order.finalPrice,
          pointsRefunded: refunded,
        },
      });

      // Send WhatsApp notification to customer
      try {
        const { data } = await supabase.functions.invoke("whatsapp-notify", {
          body: {
            type: "status_update",
            orderRef: order.orderRef,
            phone: order.phone,
            name: order.name,
            city: order.city,
            status: "cancelled",
            items: order.items,
            finalPrice: order.finalPrice,
            paymentLabel: order.paymentLabel,
            cancelReason: reason,
          },
        });
        if (data?.autoSent) {
          onToast(`تم إرسال إشعار الإلغاء للعميل ${order.name} عبر واتساب ✓`);
        } else if (data?.whatsappUrl) {
          window.open(data.whatsappUrl, "_blank", "noopener,noreferrer");
        }
      } catch { /* silent */ }

      // Send email notification for cancellation
      supabase.functions.invoke("send-order-email", {
        body: {
          orderRef: order.orderRef,
          phone: order.phone,
          name: order.name,
          status: "cancelled",
          items: order.items,
          finalPrice: order.finalPrice,
          paymentLabel: order.paymentLabel,
          city: order.city,
          cancelReason: reason,
        },
      }).then((res) => {
        if (res.data?.sent) {
          onToast(`تم إرسال إشعار الإلغاء بالبريد للعميل ${order.name} ✉️`);
        }
      }).catch(() => { /* silent */ });
    } else {
      onToast("حدث خطأ أثناء إلغاء الطلب", "error");
    }

    setCancelling(false);
    setCancelConfirm(null);
    setExpanded(null);
  };

  const isBankTransfer = (o: UserOrder) =>
    o.paymentLabel === "التحويل البنكي" || o.paymentLabel?.includes("بنك") || o.paymentLabel?.includes("تحويل");

  const hasAdvancedFilter = !!(dateFrom || dateTo || priceMin || priceMax);

  const clearAdvanced = () => {
    setDateFrom(""); setDateTo(""); setPriceMin(""); setPriceMax("");
  };

  const filtered = orders.filter((o) => {
    const matchStatus = filterStatus === "all" || o.status === filterStatus;
    const matchPayment =
      filterPayment === "all" ||
      (filterPayment === "bank_pending" && isBankTransfer(o) && o.status === "pending") ||
      (filterPayment === "bank" && isBankTransfer(o)) ||
      (filterPayment === "cash" && !isBankTransfer(o));
    const matchSearch =
      !search ||
      o.orderRef.includes(search) ||
      o.name.includes(search) ||
      o.phone.includes(search) ||
      o.city.includes(search);

    // Advanced filters
    const orderDate = new Date(o.date);
    const matchDateFrom = !dateFrom || orderDate >= new Date(dateFrom);
    const matchDateTo = !dateTo || orderDate <= new Date(dateTo + "T23:59:59");
    const matchPriceMin = !priceMin || o.finalPrice >= Number(priceMin);
    const matchPriceMax = !priceMax || o.finalPrice <= Number(priceMax);

    return matchStatus && matchPayment && matchSearch && matchDateFrom && matchDateTo && matchPriceMin && matchPriceMax;
  });

  const counts = {
    all: orders.length,
    pending: orders.filter((o) => o.status === "pending").length,
    confirmed: orders.filter((o) => o.status === "confirmed").length,
    shipped: orders.filter((o) => o.status === "shipped").length,
    delivered: orders.filter((o) => o.status === "delivered").length,
    cancelled: orders.filter((o) => o.status === "cancelled").length,
    bank_pending: orders.filter((o) => isBankTransfer(o) && o.status === "pending").length,
  };

  // ── Growth & Revenue Stats ──
  const now = new Date();
  const todayStart   = startOfDayLocal(now);
  const weekStart    = startOfWeekLocal(now);
  const monthStart   = startOfMonthLocal(now);
  const lastMonthStart = startOfLastMonthLocal(now);

  const ordersToday     = orders.filter(o => new Date(o.date) >= todayStart);
  const ordersThisWeek  = orders.filter(o => new Date(o.date) >= weekStart);
  const ordersThisMonth = orders.filter(o => new Date(o.date) >= monthStart);
  const ordersLastMonth = orders.filter(o => { const d = new Date(o.date); return d >= lastMonthStart && d < monthStart; });

  const revenueToday     = ordersToday.reduce((s, o) => s + o.finalPrice, 0);
  const revenueThisWeek  = ordersThisWeek.reduce((s, o) => s + o.finalPrice, 0);
  const revenueThisMonth = ordersThisMonth.reduce((s, o) => s + o.finalPrice, 0);
  const revenueLastMonth = ordersLastMonth.reduce((s, o) => s + o.finalPrice, 0);

  const monthOrderGrowth = ordersLastMonth.length === 0
    ? (ordersThisMonth.length > 0 ? 100 : 0)
    : Math.round(((ordersThisMonth.length - ordersLastMonth.length) / ordersLastMonth.length) * 100);

  const monthRevenueGrowth = revenueLastMonth === 0
    ? (revenueThisMonth > 0 ? 100 : 0)
    : Math.round(((revenueThisMonth - revenueLastMonth) / revenueLastMonth) * 100);

  // Conversion rate: delivered / total
  const conversionRate = orders.length === 0 ? 0 : Math.round((counts.delivered / orders.length) * 100);

  // Avg order value
  const avgOrderValue = orders.length === 0 ? 0 : Math.round(orders.reduce((s, o) => s + o.finalPrice, 0) / orders.length);

  // Last 14 days daily orders chart
  const last14Days = Array.from({ length: 14 }, (_, i) => {
    const d = new Date(now);
    d.setDate(d.getDate() - (13 - i));
    return startOfDayLocal(d);
  });
  const dailyOrderCounts = last14Days.map(dayStart => {
    const dayEnd = new Date(dayStart); dayEnd.setDate(dayEnd.getDate() + 1);
    return orders.filter(o => { const d = new Date(o.date); return d >= dayStart && d < dayEnd; }).length;
  });
  const dailyRevenue = last14Days.map(dayStart => {
    const dayEnd = new Date(dayStart); dayEnd.setDate(dayEnd.getDate() + 1);
    return orders.filter(o => { const d = new Date(o.date); return d >= dayStart && d < dayEnd; })
      .reduce((s, o) => s + o.finalPrice, 0);
  });
  const maxDailyOrders = Math.max(...dailyOrderCounts, 1);
  const maxDailyRevenue = Math.max(...dailyRevenue, 1);

  // Status distribution for donut-like display
  const statusDist = [
    { label: "قيد المعالجة", count: counts.pending,   color: "bg-amber-400",  pct: orders.length ? Math.round((counts.pending / orders.length) * 100) : 0 },
    { label: "تم التأكيد",   count: counts.confirmed, color: "bg-green-400",  pct: orders.length ? Math.round((counts.confirmed / orders.length) * 100) : 0 },
    { label: "تم الشحن",     count: counts.shipped,   color: "bg-indigo-400", pct: orders.length ? Math.round((counts.shipped / orders.length) * 100) : 0 },
    { label: "تم التوصيل",   count: counts.delivered, color: "bg-teal-400",   pct: orders.length ? Math.round((counts.delivered / orders.length) * 100) : 0 },
  ];

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-gray-400">
        <i className="ri-loader-4-line text-3xl animate-spin mb-3"></i>
        <p className="text-sm">جاري تحميل الطلبات...</p>
      </div>
    );
  }

  if (orders.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-gray-400">
        <div className="w-16 h-16 flex items-center justify-center bg-gray-100 rounded-full mb-4">
          <i className="ri-shopping-bag-line text-3xl"></i>
        </div>
        <p className="text-sm font-medium text-gray-500">لا توجد طلبات بعد</p>
        <p className="text-xs text-gray-400 mt-1">ستظهر الطلبات هنا بعد أول عملية شراء</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5">

      {/* ── Cancel Confirm Modal ── */}
      {cancelConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/50 backdrop-blur-sm" dir="rtl">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden">
            {/* Header */}
            <div className="bg-red-50 border-b border-red-100 px-6 py-4 flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center bg-red-100 rounded-xl flex-shrink-0">
                <i className="ri-close-circle-line text-red-600 text-xl"></i>
              </div>
              <div>
                <p className="text-red-700 font-black text-sm">إلغاء الطلب #{cancelConfirm.order.orderRef}</p>
                <p className="text-red-500 text-xs mt-0.5">{cancelConfirm.order.name} · {cancelConfirm.order.finalPrice.toLocaleString()} ر.س</p>
              </div>
            </div>

            {/* Body */}
            <div className="px-6 py-5 flex flex-col gap-4">
              <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-start gap-2">
                <i className="ri-error-warning-line text-amber-600 text-sm mt-0.5 flex-shrink-0"></i>
                <p className="text-amber-700 text-xs leading-relaxed">
                  سيتم إلغاء الطلب نهائياً وإرسال إشعار للعميل عبر واتساب. هذا الإجراء لا يمكن التراجع عنه.
                </p>
              </div>

              <div>
                <label className="text-gray-600 text-xs font-bold block mb-2">
                  سبب الإلغاء <span className="text-red-500">*</span>
                </label>
                <div className="flex flex-col gap-2 mb-3">
                  {[
                    "المنتج غير متوفر في المخزون",
                    "طلب العميل الإلغاء",
                    "عدم تأكيد التحويل البنكي",
                    "بيانات الطلب غير صحيحة",
                    "مشكلة في التوصيل للمنطقة",
                  ].map((reason) => (
                    <button
                      key={reason}
                      type="button"
                      onClick={() => setCancelConfirm({ ...cancelConfirm, reason })}
                      className={`text-right text-xs px-3 py-2.5 rounded-lg border transition-all cursor-pointer ${
                        cancelConfirm.reason === reason
                          ? "bg-red-50 border-red-300 text-red-700 font-bold"
                          : "bg-gray-50 border-gray-200 text-gray-600 hover:border-gray-300"
                      }`}
                    >
                      {cancelConfirm.reason === reason && <i className="ri-check-line ml-1 text-red-500"></i>}
                      {reason}
                    </button>
                  ))}
                </div>
                <input
                  type="text"
                  value={cancelConfirm.reason}
                  onChange={(e) => setCancelConfirm({ ...cancelConfirm, reason: e.target.value })}
                  placeholder="أو اكتب سبباً مخصصاً..."
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-red-300 transition-colors placeholder-gray-400"
                />
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 pb-5 flex gap-3">
              <button
                onClick={handleCancelOrder}
                disabled={!cancelConfirm.reason.trim() || cancelling}
                className="flex-1 flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-black text-sm py-3 rounded-xl transition-colors cursor-pointer whitespace-nowrap"
              >
                {cancelling ? (
                  <><i className="ri-loader-4-line animate-spin text-base"></i>جاري الإلغاء...</>
                ) : (
                  <><i className="ri-close-circle-line text-base"></i>تأكيد الإلغاء</>
                )}
              </button>
              <button
                onClick={() => setCancelConfirm(null)}
                disabled={cancelling}
                className="px-5 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold text-sm rounded-xl transition-colors cursor-pointer whitespace-nowrap"
              >
                تراجع
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══ STATS SECTION ══ */}
      {/* Growth Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {/* Today */}
        <div className="bg-white rounded-xl p-4 border border-gray-100 flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <div className="w-8 h-8 flex items-center justify-center bg-amber-50 rounded-lg">
              <i className="ri-shopping-bag-3-line text-amber-600 text-sm"></i>
            </div>
            <span className="text-[10px] font-bold text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">اليوم</span>
          </div>
          <p className="text-2xl font-black text-[#1a1a1a]">{ordersToday.length}</p>
          <p className="text-xs text-gray-400">طلب اليوم</p>
          {revenueToday > 0 && (
            <p className="text-[11px] font-bold text-amber-600">{revenueToday.toLocaleString("ar-SA")} ر.س</p>
          )}
        </div>

        {/* This Week */}
        <div className="bg-white rounded-xl p-4 border border-gray-100 flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <div className="w-8 h-8 flex items-center justify-center bg-green-50 rounded-lg">
              <i className="ri-calendar-check-line text-green-600 text-sm"></i>
            </div>
            <span className="text-[10px] font-bold text-green-600 bg-green-50 px-2 py-0.5 rounded-full">الأسبوع</span>
          </div>
          <p className="text-2xl font-black text-[#1a1a1a]">{ordersThisWeek.length}</p>
          <p className="text-xs text-gray-400">طلب هذا الأسبوع</p>
          {revenueThisWeek > 0 && (
            <p className="text-[11px] font-bold text-green-600">{revenueThisWeek.toLocaleString("ar-SA")} ر.س</p>
          )}
        </div>

        {/* This Month */}
        <div className="bg-white rounded-xl p-4 border border-gray-100 flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <div className="w-8 h-8 flex items-center justify-center bg-[#1a1a1a]/5 rounded-lg">
              <i className="ri-calendar-2-line text-[#1a1a1a] text-sm"></i>
            </div>
            <div className="flex items-center gap-1">
              {monthOrderGrowth !== 0 && (
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full flex items-center gap-0.5 ${
                  monthOrderGrowth > 0 ? "text-green-600 bg-green-50" : "text-red-500 bg-red-50"
                }`}>
                  <i className={`text-[10px] ${monthOrderGrowth > 0 ? "ri-arrow-up-line" : "ri-arrow-down-line"}`}></i>
                  {Math.abs(monthOrderGrowth)}%
                </span>
              )}
            </div>
          </div>
          <p className="text-2xl font-black text-[#1a1a1a]">{ordersThisMonth.length}</p>
          <p className="text-xs text-gray-400">طلب هذا الشهر</p>
          {ordersLastMonth.length > 0 && (
            <p className="text-[10px] text-gray-400">الشهر الماضي: <span className="font-bold text-gray-500">{ordersLastMonth.length}</span></p>
          )}
        </div>

        {/* Revenue This Month */}
        <div className="bg-[#1a1a1a] rounded-xl p-4 flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <div className="w-8 h-8 flex items-center justify-center bg-white/10 rounded-lg">
              <i className="ri-money-dollar-circle-line text-white text-sm"></i>
            </div>
            {monthRevenueGrowth !== 0 && (
              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full flex items-center gap-0.5 ${
                monthRevenueGrowth > 0 ? "text-green-400 bg-green-400/10" : "text-red-400 bg-red-400/10"
              }`}>
                <i className={`text-[10px] ${monthRevenueGrowth > 0 ? "ri-arrow-up-line" : "ri-arrow-down-line"}`}></i>
                {Math.abs(monthRevenueGrowth)}%
              </span>
            )}
          </div>
          <p className="text-xl font-black text-white leading-tight">{revenueThisMonth.toLocaleString("ar-SA")}</p>
          <p className="text-xs text-white/50">ر.س إيرادات الشهر</p>
          {revenueLastMonth > 0 && (
            <p className="text-[10px] text-white/40">الشهر الماضي: {revenueLastMonth.toLocaleString("ar-SA")}</p>
          )}
        </div>
      </div>

      {/* KPI Row */}
      <div className="grid grid-cols-3 gap-3">
        {/* Conversion Rate */}
        <div className="bg-white rounded-xl p-4 border border-gray-100">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-bold text-gray-500">نسبة التحويل</p>
            <div className="w-7 h-7 flex items-center justify-center bg-teal-50 rounded-lg">
              <i className="ri-percent-line text-teal-600 text-xs"></i>
            </div>
          </div>
          {/* Progress arc */}
          <div className="flex items-end gap-3">
            <p className="text-2xl font-black text-[#1a1a1a]">{conversionRate}%</p>
            <p className="text-xs text-gray-400 mb-1 leading-tight">من الطلبات<br/>تم توصيلها</p>
          </div>
          <div className="mt-3 h-2 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-teal-400 rounded-full transition-all duration-700"
              style={{ width: `${conversionRate}%` }}
            ></div>
          </div>
          <p className="text-[10px] text-gray-400 mt-1">{counts.delivered} من {orders.length} طلب</p>
        </div>

        {/* Avg Order Value */}
        <div className="bg-white rounded-xl p-4 border border-gray-100">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-bold text-gray-500">متوسط قيمة الطلب</p>
            <div className="w-7 h-7 flex items-center justify-center bg-amber-50 rounded-lg">
              <i className="ri-bar-chart-box-line text-amber-600 text-xs"></i>
            </div>
          </div>
          <p className="text-2xl font-black text-[#1a1a1a]">{avgOrderValue.toLocaleString("ar-SA")}</p>
          <p className="text-xs text-gray-400 mt-1">ر.س لكل طلب</p>
          <div className="mt-3 flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-full bg-amber-400"></div>
            <p className="text-[10px] text-gray-400">بناءً على {orders.length} طلب</p>
          </div>
        </div>

        {/* Status Distribution */}
        <div className="bg-white rounded-xl p-4 border border-gray-100">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-bold text-gray-500">توزيع الحالات</p>
            <div className="w-7 h-7 flex items-center justify-center bg-gray-50 rounded-lg">
              <i className="ri-pie-chart-line text-gray-500 text-xs"></i>
            </div>
          </div>
          <div className="flex flex-col gap-1.5">
            {statusDist.filter(s => s.count > 0).map(s => (
              <div key={s.label} className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full flex-shrink-0 ${s.color}`}></div>
                <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <div className={`h-full ${s.color} rounded-full`} style={{ width: `${s.pct}%` }}></div>
                </div>
                <span className="text-[10px] text-gray-500 font-bold w-6 text-left">{s.pct}%</span>
              </div>
            ))}
            {statusDist.every(s => s.count === 0) && (
              <p className="text-xs text-gray-300 text-center py-2">لا توجد طلبات</p>
            )}
          </div>
          <div className="mt-2 flex flex-wrap gap-1">
            {statusDist.filter(s => s.count > 0).map(s => (
              <span key={s.label} className="text-[9px] text-gray-400">{s.label} ({s.count})</span>
            ))}
          </div>
        </div>
      </div>

      {/* Daily Chart — last 14 days */}
      <div className="bg-white rounded-xl border border-gray-100 p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-sm font-bold text-[#1a1a1a]">الطلبات اليومية</p>
            <p className="text-xs text-gray-400 mt-0.5">آخر 14 يوم — الأعمدة الداكنة: الإيرادات</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5">
              <div className="w-2.5 h-2.5 rounded-sm bg-[#1a1a1a]"></div>
              <span className="text-[10px] text-gray-400">طلبات</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-2.5 h-2.5 rounded-sm bg-amber-300"></div>
              <span className="text-[10px] text-gray-400">إيرادات</span>
            </div>
          </div>
        </div>

        <div className="flex items-end gap-1 h-24">
          {last14Days.map((day, i) => {
            const orderH = maxDailyOrders === 0 ? 0 : (dailyOrderCounts[i] / maxDailyOrders) * 100;
            const revenueH = maxDailyRevenue === 0 ? 0 : (dailyRevenue[i] / maxDailyRevenue) * 100;
            const isToday = i === 13;
            return (
              <div key={i} className="flex-1 flex items-end gap-0.5 group relative" style={{ height: "100%" }}>
                {/* Revenue bar */}
                <div
                  className={`flex-1 rounded-sm transition-all ${isToday ? "bg-amber-400" : "bg-amber-200 group-hover:bg-amber-300"}`}
                  style={{ height: `${Math.max(revenueH, dailyRevenue[i] > 0 ? 6 : 1)}%` }}
                ></div>
                {/* Orders bar */}
                <div
                  className={`flex-1 rounded-sm transition-all ${isToday ? "bg-[#1a1a1a]" : "bg-gray-300 group-hover:bg-gray-400"}`}
                  style={{ height: `${Math.max(orderH, dailyOrderCounts[i] > 0 ? 6 : 1)}%` }}
                ></div>
                {/* Tooltip */}
                {(dailyOrderCounts[i] > 0 || dailyRevenue[i] > 0) && (
                  <div className="absolute bottom-full mb-1 left-1/2 -translate-x-1/2 bg-[#1a1a1a] text-white text-[9px] px-2 py-1 rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10 text-center">
                    <p className="font-bold">{dailyOrderCounts[i]} طلب</p>
                    {dailyRevenue[i] > 0 && <p className="text-gray-300">{dailyRevenue[i].toLocaleString("ar-SA")} ر.س</p>}
                    <p className="text-gray-400">{day.toLocaleDateString("ar-SA", { month: "short", day: "numeric" })}</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* X-axis */}
        <div className="flex justify-between mt-2">
          <span className="text-[10px] text-gray-400">
            {last14Days[0].toLocaleDateString("ar-SA", { month: "short", day: "numeric" })}
          </span>
          <span className="text-[10px] text-[#1a1a1a] font-bold">اليوم</span>
        </div>
      </div>

      {/* ══ END STATS ══ */}

      {/* WhatsApp Auto-Send Result Banner */}
      {waResult && (
        <div className={`flex items-start gap-3 px-4 py-3 rounded-xl border text-sm ${
          waResult.autoSent
            ? "bg-green-50 border-green-200 text-green-700"
            : !waResult.apiConfigured
            ? "bg-amber-50 border-amber-200 text-amber-700"
            : "bg-red-50 border-red-200 text-red-700"
        }`}>
          <i className={`text-base mt-0.5 flex-shrink-0 ${
            waResult.autoSent ? "ri-whatsapp-line" : "ri-information-line"
          }`}></i>
          <div className="flex-1 min-w-0">
            {waResult.autoSent ? (
              <p className="font-bold text-xs">تم إرسال إشعار واتساب تلقائياً للعميل ✓</p>
            ) : !waResult.apiConfigured ? (
              <div>
                <p className="font-bold text-xs mb-1">إشعار واتساب — يحتاج إعداد CallMeBot API</p>
                <p className="text-[11px] opacity-80 mb-2">
                  لتفعيل الإرسال التلقائي، أضف <strong>CALLMEBOT_API_KEY</strong> في إعدادات واتساب
                </p>
                {waResult.whatsappUrl && (
                  <a
                    href={waResult.whatsappUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-[11px] font-bold text-green-700 hover:underline cursor-pointer"
                  >
                    <i className="ri-whatsapp-line"></i>
                    إرسال يدوي للعميل عبر واتساب
                  </a>
                )}
              </div>
            ) : (
              <p className="text-xs">تعذّر الإرسال التلقائي — تحقق من إعدادات CallMeBot</p>
            )}
          </div>
          <button onClick={() => setWaResult(null)} className="text-current opacity-50 hover:opacity-100 cursor-pointer flex-shrink-0">
            <i className="ri-close-line text-sm"></i>
          </button>
        </div>
      )}

      {/* SMS Result Banner */}
      {smsResult && (
        <div className={`flex items-start gap-3 px-4 py-3 rounded-xl border text-sm ${
          smsResult.sent
            ? "bg-green-50 border-green-200 text-green-700"
            : smsResult.setupRequired
            ? "bg-amber-50 border-amber-200 text-amber-700"
            : "bg-gray-50 border-gray-200 text-gray-600"
        }`}>
          <i className={`text-base mt-0.5 flex-shrink-0 ${
            smsResult.sent ? "ri-message-2-line" : smsResult.setupRequired ? "ri-settings-3-line" : "ri-error-warning-line"
          }`}></i>
          <div className="flex-1 min-w-0">
            {smsResult.sent ? (
              <p className="font-bold text-xs">تم إرسال SMS للعميل بنجاح ✓</p>
            ) : smsResult.setupRequired ? (
              <div>
                <p className="font-bold text-xs mb-1">يحتاج إعداد Twilio لإرسال SMS</p>
                <p className="text-[11px] opacity-80">أضف TWILIO_ACCOUNT_SID و TWILIO_AUTH_TOKEN و TWILIO_FROM_NUMBER في Supabase Secrets</p>
                {smsResult.fallbackMsg && (
                  <details className="mt-2">
                    <summary className="text-[11px] cursor-pointer font-bold">عرض نص الرسالة للإرسال اليدوي</summary>
                    <div className="mt-1 bg-white rounded-lg p-2 border border-amber-200">
                      <p className="text-[11px] text-gray-700 whitespace-pre-wrap">{smsResult.fallbackMsg}</p>
                      {smsResult.fallbackPhone && (
                        <a
                          href={`https://wa.me/${smsResult.fallbackPhone.replace("+", "")}?text=${encodeURIComponent(smsResult.fallbackMsg)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-2 flex items-center gap-1 text-green-600 text-[11px] font-bold hover:underline cursor-pointer"
                        >
                          <i className="ri-whatsapp-line"></i>
                          إرسال عبر واتساب بدلاً من ذلك
                        </a>
                      )}
                    </div>
                  </details>
                )}
              </div>
            ) : (
              <p className="text-xs">فشل إرسال SMS — تحقق من إعدادات Twilio</p>
            )}
          </div>
          <button onClick={() => setSmsResult(null)} className="text-current opacity-50 hover:opacity-100 cursor-pointer flex-shrink-0">
            <i className="ri-close-line text-sm"></i>
          </button>
        </div>
      )}

      {/* Bank Pending Alert Banner */}
      {counts.bank_pending > 0 && filterPayment !== "bank_pending" && (
        <button
          onClick={() => { setFilterPayment("bank_pending"); setFilterStatus("all"); }}
          className="w-full flex items-center gap-3 bg-orange-50 border-2 border-orange-300 rounded-xl px-4 py-3 text-right cursor-pointer hover:bg-orange-100 transition-colors"
        >
          <div className="w-9 h-9 flex items-center justify-center bg-orange-400 rounded-xl flex-shrink-0 animate-pulse">
            <i className="ri-bank-line text-white text-base"></i>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-orange-800 text-sm font-black">
              {counts.bank_pending} طلب تحويل بنكي بانتظار التأكيد
            </p>
            <p className="text-orange-600 text-xs mt-0.5">
              اضغط لعرض هذه الطلبات وتأكيد استلام التحويل
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className="bg-orange-400 text-white text-xs font-black px-2.5 py-1 rounded-full">
              {counts.bank_pending}
            </span>
            <i className="ri-arrow-left-line text-orange-500 text-sm"></i>
          </div>
        </button>
      )}

      {/* Stats bar */}
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
        {[
          { key: "all",       label: "الكل",           icon: "ri-list-check",           color: "text-gray-700",    bg: "bg-gray-50",    border: "border-gray-200" },
          { key: "pending",   label: "قيد المعالجة",   icon: "ri-time-line",            color: "text-amber-700",   bg: "bg-amber-50",   border: "border-amber-200" },
          { key: "confirmed", label: "تم التأكيد",     icon: "ri-checkbox-circle-line", color: "text-green-700",   bg: "bg-green-50",   border: "border-green-200" },
          { key: "shipped",   label: "تم الشحن",       icon: "ri-send-plane-line",      color: "text-indigo-700",  bg: "bg-indigo-50",  border: "border-indigo-200" },
          { key: "delivered", label: "تم التوصيل",     icon: "ri-truck-line",           color: "text-teal-700",    bg: "bg-teal-50",    border: "border-teal-200" },
          { key: "cancelled", label: "ملغي",           icon: "ri-close-circle-line",    color: "text-red-700",     bg: "bg-red-50",     border: "border-red-200" },
        ].map((s) => (
          <button
            key={s.key}
            onClick={() => { setFilterStatus(s.key); setFilterPayment("all"); }}
            className={`flex flex-col items-center gap-1 p-3 rounded-xl border transition-all cursor-pointer ${
              filterStatus === s.key && filterPayment === "all"
                ? `${s.bg} ${s.border} ${s.color}`
                : "bg-white border-gray-100 text-gray-400 hover:border-gray-200"
            }`}
          >
            <i className={`${s.icon} text-lg`}></i>
            <span className="text-lg font-black">{counts[s.key as keyof typeof counts]}</span>
            <span className="text-[10px] font-medium whitespace-nowrap">{s.label}</span>
          </button>
        ))}
      </div>

      {/* Payment Filter Row */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs text-gray-400 font-medium whitespace-nowrap">فلتر الدفع:</span>
        {[
          { key: "all",          label: "الكل",                    icon: "ri-filter-line",   color: "text-gray-600",   bg: "bg-gray-50",    border: "border-gray-200" },
          { key: "bank_pending", label: `تحويل معلق (${counts.bank_pending})`, icon: "ri-bank-line", color: "text-orange-700", bg: "bg-orange-50",  border: "border-orange-300" },
          { key: "bank",         label: "تحويل بنكي",              icon: "ri-bank-line",     color: "text-amber-700",  bg: "bg-amber-50",   border: "border-amber-200" },
          { key: "cash",         label: "دفع عند الاستلام",        icon: "ri-money-dollar-circle-line", color: "text-green-700", bg: "bg-green-50", border: "border-green-200" },
        ].map((p) => (
          <button
            key={p.key}
            onClick={() => { setFilterPayment(p.key); setFilterStatus("all"); }}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${
              filterPayment === p.key
                ? `${p.bg} ${p.border} ${p.color}`
                : "bg-white border-gray-100 text-gray-400 hover:border-gray-200"
            } ${p.key === "bank_pending" && counts.bank_pending > 0 ? "ring-1 ring-orange-300" : ""}`}
          >
            <i className={`${p.icon} text-xs`}></i>
            {p.label}
          </button>
        ))}
      </div>

      {/* Search + Advanced toggle */}
      <div className="flex gap-2">
        <div className="relative flex-1">
          <i className="ri-search-line absolute top-1/2 -translate-y-1/2 right-3 text-gray-300 text-sm"></i>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="بحث برقم الطلب، الاسم، الجوال، المدينة..."
            className="w-full border border-gray-200 rounded-xl pr-9 pl-4 py-2.5 text-sm outline-none focus:border-gray-400 transition-colors"
          />
          {search && (
            <button
              onClick={() => setSearch("")}
              className="absolute top-1/2 -translate-y-1/2 left-3 text-gray-400 hover:text-gray-600 cursor-pointer"
            >
              <i className="ri-close-line text-sm"></i>
            </button>
          )}
        </div>
        <button
          onClick={() => setShowAdvanced(v => !v)}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border text-sm font-bold transition-all cursor-pointer whitespace-nowrap ${
            showAdvanced || hasAdvancedFilter
              ? "bg-[#1a1a1a] border-[#1a1a1a] text-white"
              : "bg-white border-gray-200 text-gray-500 hover:border-gray-400 hover:text-gray-700"
          }`}
        >
          <i className="ri-equalizer-3-line text-sm"></i>
          <span className="hidden sm:block">فلتر متقدم</span>
          {hasAdvancedFilter && (
            <span className="w-2 h-2 rounded-full bg-amber-400 flex-shrink-0"></span>
          )}
        </button>
      </div>

      {/* Advanced Filter Panel */}
      {showAdvanced && (
        <div className="bg-gray-50 border border-gray-200 rounded-2xl p-4 flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <p className="text-sm font-bold text-[#1a1a1a] flex items-center gap-2">
              <i className="ri-equalizer-3-line text-gray-500"></i>
              فلتر متقدم
            </p>
            {hasAdvancedFilter && (
              <button
                onClick={clearAdvanced}
                className="flex items-center gap-1.5 text-xs font-bold text-red-500 hover:text-red-700 cursor-pointer transition-colors"
              >
                <i className="ri-refresh-line text-xs"></i>
                مسح الفلاتر
              </button>
            )}
          </div>

          {/* Date range */}
          <div>
            <p className="text-xs font-bold text-gray-500 mb-2 flex items-center gap-1.5">
              <i className="ri-calendar-line text-gray-400"></i>
              نطاق التاريخ
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] text-gray-400 block mb-1">من تاريخ</label>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={e => setDateFrom(e.target.value)}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-[#1a1a1a] transition-colors bg-white cursor-pointer"
                />
              </div>
              <div>
                <label className="text-[11px] text-gray-400 block mb-1">إلى تاريخ</label>
                <input
                  type="date"
                  value={dateTo}
                  onChange={e => setDateTo(e.target.value)}
                  min={dateFrom}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-[#1a1a1a] transition-colors bg-white cursor-pointer"
                />
              </div>
            </div>
            {/* Quick date shortcuts */}
            <div className="flex flex-wrap gap-1.5 mt-2">
              {[
                { label: "اليوم", action: () => { const t = new Date().toISOString().split("T")[0]; setDateFrom(t); setDateTo(t); } },
                { label: "هذا الأسبوع", action: () => { const now = new Date(); const mon = new Date(now); mon.setDate(now.getDate() - now.getDay()); setDateFrom(mon.toISOString().split("T")[0]); setDateTo(now.toISOString().split("T")[0]); } },
                { label: "هذا الشهر", action: () => { const now = new Date(); setDateFrom(`${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,"0")}-01`); setDateTo(now.toISOString().split("T")[0]); } },
                { label: "آخر 30 يوم", action: () => { const now = new Date(); const past = new Date(now); past.setDate(now.getDate()-30); setDateFrom(past.toISOString().split("T")[0]); setDateTo(now.toISOString().split("T")[0]); } },
              ].map(s => (
                <button
                  key={s.label}
                  onClick={s.action}
                  className="text-[11px] font-bold px-2.5 py-1 rounded-full border border-gray-200 bg-white text-gray-600 hover:border-[#1a1a1a] hover:text-[#1a1a1a] transition-all cursor-pointer whitespace-nowrap"
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          {/* Price range */}
          <div>
            <p className="text-xs font-bold text-gray-500 mb-2 flex items-center gap-1.5">
              <i className="ri-money-dollar-circle-line text-gray-400"></i>
              نطاق السعر (ر.س)
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] text-gray-400 block mb-1">الحد الأدنى</label>
                <input
                  type="number"
                  value={priceMin}
                  onChange={e => setPriceMin(e.target.value)}
                  placeholder="0"
                  min="0"
                  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-[#1a1a1a] transition-colors bg-white"
                />
              </div>
              <div>
                <label className="text-[11px] text-gray-400 block mb-1">الحد الأقصى</label>
                <input
                  type="number"
                  value={priceMax}
                  onChange={e => setPriceMax(e.target.value)}
                  placeholder="غير محدد"
                  min="0"
                  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-[#1a1a1a] transition-colors bg-white"
                />
              </div>
            </div>
            {/* Quick price shortcuts */}
            <div className="flex flex-wrap gap-1.5 mt-2">
              {[
                { label: "أقل من 100", action: () => { setPriceMin(""); setPriceMax("100"); } },
                { label: "100 - 500", action: () => { setPriceMin("100"); setPriceMax("500"); } },
                { label: "500 - 1000", action: () => { setPriceMin("500"); setPriceMax("1000"); } },
                { label: "أكثر من 1000", action: () => { setPriceMin("1000"); setPriceMax(""); } },
              ].map(s => (
                <button
                  key={s.label}
                  onClick={s.action}
                  className="text-[11px] font-bold px-2.5 py-1 rounded-full border border-gray-200 bg-white text-gray-600 hover:border-[#1a1a1a] hover:text-[#1a1a1a] transition-all cursor-pointer whitespace-nowrap"
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          {/* Active filters summary */}
          {hasAdvancedFilter && (
            <div className="flex flex-wrap gap-2 pt-1 border-t border-gray-200">
              <p className="text-[11px] text-gray-400 w-full">الفلاتر المفعّلة:</p>
              {dateFrom && (
                <span className="flex items-center gap-1 text-[11px] font-bold bg-[#1a1a1a] text-white px-2.5 py-1 rounded-full">
                  <i className="ri-calendar-line text-[10px]"></i>
                  من: {new Date(dateFrom).toLocaleDateString("ar-SA", { month: "short", day: "numeric" })}
                  <button onClick={() => setDateFrom("")} className="mr-0.5 opacity-70 hover:opacity-100 cursor-pointer"><i className="ri-close-line text-[10px]"></i></button>
                </span>
              )}
              {dateTo && (
                <span className="flex items-center gap-1 text-[11px] font-bold bg-[#1a1a1a] text-white px-2.5 py-1 rounded-full">
                  <i className="ri-calendar-line text-[10px]"></i>
                  إلى: {new Date(dateTo).toLocaleDateString("ar-SA", { month: "short", day: "numeric" })}
                  <button onClick={() => setDateTo("")} className="mr-0.5 opacity-70 hover:opacity-100 cursor-pointer"><i className="ri-close-line text-[10px]"></i></button>
                </span>
              )}
              {priceMin && (
                <span className="flex items-center gap-1 text-[11px] font-bold bg-[#1a1a1a] text-white px-2.5 py-1 rounded-full">
                  من {Number(priceMin).toLocaleString()} ر.س
                  <button onClick={() => setPriceMin("")} className="mr-0.5 opacity-70 hover:opacity-100 cursor-pointer"><i className="ri-close-line text-[10px]"></i></button>
                </span>
              )}
              {priceMax && (
                <span className="flex items-center gap-1 text-[11px] font-bold bg-[#1a1a1a] text-white px-2.5 py-1 rounded-full">
                  إلى {Number(priceMax).toLocaleString()} ر.س
                  <button onClick={() => setPriceMax("")} className="mr-0.5 opacity-70 hover:opacity-100 cursor-pointer"><i className="ri-close-line text-[10px]"></i></button>
                </span>
              )}
            </div>
          )}
        </div>
      )}

      {/* Orders list */}
      {filtered.length === 0 ? (
        <div className="text-center py-12 text-gray-400 text-sm">لا توجد نتائج</div>
      ) : (
        <div className="flex flex-col gap-3">
          {filtered.map((order) => {
            const st = STATUS_OPTIONS.find((s) => s.value === order.status) || STATUS_OPTIONS[0];
            const isOpen = expanded === order.orderRef;
            const orderDate = new Date(order.date).toLocaleDateString("ar-SA", {
              year: "numeric", month: "short", day: "numeric",
            });
            const orderTime = new Date(order.date).toLocaleTimeString("ar-SA", {
              hour: "2-digit", minute: "2-digit",
            });
            const isSending = sendingNotification === order.orderRef;

            const isBankPending = isBankTransfer(order) && order.status === "pending";

            return (
              <div key={order.orderRef} className={`bg-white rounded-2xl overflow-hidden border-2 transition-all ${
                isBankPending
                  ? "border-orange-300 ring-1 ring-orange-200"
                  : "border-gray-100"
              }`}>
                {isBankPending && (
                  <div className="flex items-center justify-between gap-3 bg-orange-50 border-b border-orange-200 px-5 py-2.5">
                    <div className="flex items-center gap-2">
                      <i className="ri-error-warning-line text-orange-500 text-sm flex-shrink-0"></i>
                      <p className="text-orange-700 text-xs font-bold">
                        بانتظار تأكيد استلام التحويل البنكي
                      </p>
                    </div>
                    <button
                      onClick={() => handleStatusChange(order.orderRef, "confirmed")}
                      disabled={isSending}
                      className="flex items-center gap-1.5 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white text-xs font-black px-3 py-1.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-checkbox-circle-line text-sm"></i>
                      تأكيد التحويل
                    </button>
                  </div>
                )}
                <div className="flex items-center gap-3 px-5 py-4">
                  <button
                    onClick={() => setExpanded(isOpen ? null : order.orderRef)}
                    className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors cursor-pointer flex-shrink-0"
                  >
                    <i className={`${isOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} text-gray-400 text-base`}></i>
                  </button>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-black text-[#1a1a1a] text-sm">#{order.orderRef}</span>
                      <span className={`inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full border ${st.bg} ${st.border} ${st.color}`}>
                        <i className={`${st.icon} text-xs`}></i>
                        {st.label}
                      </span>
                      {order.status === "delivered" && order.pointsEarned && order.pointsEarned > 0 && (
                        <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full border bg-amber-50 border-amber-200 text-amber-700">
                          <i className="ri-vip-crown-line text-xs"></i>
                          +{order.pointsEarned} نقطة
                        </span>
                      )}
                      {isBankTransfer(order) && (
                        <span className={`inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full border ${
                          order.status === "pending"
                            ? "bg-orange-50 border-orange-300 text-orange-700"
                            : "bg-amber-50 border-amber-200 text-amber-700"
                        }`}>
                          <i className="ri-bank-line text-xs"></i>
                          {order.status === "pending" ? "تحويل — بانتظار التأكيد" : "تحويل بنكي"}
                        </span>
                      )}
                    </div>
                    <p className="text-gray-400 text-xs mt-0.5">
                      {order.name} · {order.phone} · {order.city} · {orderDate} {orderTime}
                    </p>
                  </div>

                  <div className="text-left flex-shrink-0 hidden sm:block">
                    <p className="font-black text-[#1a1a1a] text-sm">{order.finalPrice.toLocaleString()} ر.س</p>
                    <p className="text-gray-400 text-xs">{order.items.length} منتج</p>
                  </div>

                  <div className="flex items-center gap-2 flex-shrink-0">
                    {isSending && (
                      <div className="flex items-center gap-1 text-xs text-gray-400">
                        <i className="ri-loader-4-line animate-spin text-sm"></i>
                        <span className="hidden sm:inline">إرسال SMS...</span>
                      </div>
                    )}
                    {/* Open detail page */}
                    <button
                      onClick={() => navigate(`/admin/order/${order.orderRef}`)}
                      className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-50 hover:bg-gray-100 border border-gray-200 text-gray-500 hover:text-[#1a1a1a] transition-colors cursor-pointer flex-shrink-0"
                      title="فتح صفحة تفاصيل الطلب"
                    >
                      <i className="ri-external-link-line text-sm"></i>
                    </button>
                    {order.status === "cancelled" ? (
                      <span className="flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-lg bg-red-50 border border-red-200 text-red-600">
                        <i className="ri-close-circle-line text-sm"></i>
                        ملغي
                      </span>
                    ) : (
                      <select
                        value={order.status}
                        onChange={(e) => handleStatusChange(order.orderRef, e.target.value as SavedOrder["status"])}
                        disabled={isSending}
                        className={`text-xs font-bold border rounded-lg px-2 py-1.5 outline-none cursor-pointer transition-colors ${st.bg} ${st.border} ${st.color} disabled:opacity-50`}
                      >
                        {STATUS_OPTIONS.filter(s => s.value !== "cancelled").map((s) => (
                          <option key={s.value} value={s.value}>{s.label}</option>
                        ))}
                      </select>
                    )}
                  </div>
                </div>

                {isOpen && (
                  <div className="border-t border-gray-50 px-5 py-4 flex flex-col gap-4 bg-gray-50/50">
                    <div>
                      <p className="text-xs font-bold text-gray-400 mb-2">المنتجات</p>
                      <div className="flex flex-col gap-2">
                        {order.items.map((item, idx) => (
                          <div key={idx} className="flex items-center gap-3 bg-white rounded-xl px-3 py-2.5 border border-gray-100">
                            <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 bg-gray-100">
                              <img src={item.image} alt={item.nameAr} className="w-full h-full object-cover object-top" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-[#1a1a1a] text-xs font-bold truncate">{item.nameAr}</p>
                              <p className="text-gray-400 text-[11px]">× {item.qty}</p>
                            </div>
                            <p className="text-[#1a1a1a] text-xs font-bold flex-shrink-0">
                              {(item.price * item.qty).toLocaleString()} ر.س
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      <div className="bg-white rounded-xl p-3 border border-gray-100">
                        <p className="text-gray-400 text-[10px] mb-1">طريقة الدفع</p>
                        <p className="text-[#1a1a1a] text-xs font-bold">{order.paymentLabel}</p>
                      </div>
                      <div className="bg-white rounded-xl p-3 border border-gray-100">
                        <p className="text-gray-400 text-[10px] mb-1">الإجمالي</p>
                        <p className="text-[#1a1a1a] text-xs font-bold">{order.finalPrice.toLocaleString()} ر.س</p>
                      </div>
                      {order.discountCode && (
                        <div className="bg-white rounded-xl p-3 border border-gray-100">
                          <p className="text-gray-400 text-[10px] mb-1">كود الخصم</p>
                          <p className="text-green-600 text-xs font-bold">{order.discountCode} ({order.discountPercent}%)</p>
                        </div>
                      )}
                      <div className="bg-white rounded-xl p-3 border border-gray-100">
                        <p className="text-gray-400 text-[10px] mb-1">المدينة</p>
                        <p className="text-[#1a1a1a] text-xs font-bold">{order.city}</p>
                      </div>
                    </div>

                    <div className="bg-white rounded-xl px-4 py-3 border border-gray-100 flex items-start gap-2">
                      <i className="ri-map-pin-line text-gray-400 text-sm mt-0.5 flex-shrink-0"></i>
                      <div>
                        <p className="text-[#1a1a1a] text-xs font-medium">{order.address}</p>
                        {order.notes && (
                          <p className="text-gray-400 text-[11px] mt-1">ملاحظة: {order.notes}</p>
                        )}
                      </div>
                    </div>

                    {/* Notification Buttons */}
                    <div className="bg-white rounded-xl p-3 border border-gray-100">
                      <p className="text-[10px] font-bold text-gray-400 mb-2 flex items-center gap-1">
                        <i className="ri-notification-3-line"></i>
                        إشعارات العميل
                      </p>
                      <div className="grid grid-cols-3 gap-2">
                        {/* SMS Manual */}
                        <button
                          onClick={async () => {
                            setSendingNotification(order.orderRef);
                            const sms = await sendSmsNotification(order, order.status);
                            setSmsResult({ orderRef: order.orderRef, ...sms });
                            if (sms.sent) onToast(`تم إرسال SMS للعميل ✓`);
                            setSendingNotification(null);
                          }}
                          disabled={isSending}
                          className="flex flex-col items-center gap-1 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 disabled:opacity-50 text-indigo-700 text-[10px] font-bold py-2 rounded-lg transition-colors cursor-pointer"
                        >
                          {isSending ? (
                            <i className="ri-loader-4-line animate-spin text-sm"></i>
                          ) : (
                            <i className="ri-message-2-line text-sm"></i>
                          )}
                          SMS
                        </button>

                        {/* WhatsApp Customer */}
                        <button
                          onClick={() => handleSendCustomerNotification(order)}
                          disabled={isSending}
                          className="flex flex-col items-center gap-1 bg-green-50 hover:bg-green-100 border border-green-200 disabled:opacity-50 text-green-700 text-[10px] font-bold py-2 rounded-lg transition-colors cursor-pointer"
                        >
                          <i className="ri-whatsapp-line text-sm"></i>
                          واتساب
                        </button>

                        {/* WhatsApp Admin */}
                        <button
                          onClick={() => handleSendAdminNotification(order)}
                          disabled={isSending}
                          className="flex flex-col items-center gap-1 bg-gray-50 hover:bg-gray-100 border border-gray-200 disabled:opacity-50 text-gray-700 text-[10px] font-bold py-2 rounded-lg transition-colors cursor-pointer"
                        >
                          <i className="ri-shield-user-line text-sm"></i>
                          للإدارة
                        </button>
                      </div>
                    </div>

                    <a
                      href={`https://wa.me/${order.phone.replace(/^0/, "966")}?text=${encodeURIComponent(`مرحباً ${order.name}، بخصوص طلبك #${order.orderRef}`)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2 bg-white border-2 border-green-500 text-green-600 hover:bg-green-50 text-xs font-bold py-2.5 rounded-xl transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-chat-3-line text-base"></i>
                      محادثة مباشرة مع العميل
                    </a>

                    {/* Cancel Order Button — only if not already cancelled/delivered */}
                    {order.status !== "cancelled" && order.status !== "delivered" && (
                      <button
                        onClick={() => setCancelConfirm({ order, reason: "" })}
                        disabled={isSending}
                        className="flex items-center justify-center gap-2 bg-red-50 hover:bg-red-100 border-2 border-red-300 text-red-600 text-xs font-bold py-2.5 rounded-xl transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                      >
                        <i className="ri-close-circle-line text-base"></i>
                        إلغاء الطلب
                      </button>
                    )}

                    {/* Cancelled badge */}
                    {order.status === "cancelled" && (
                      <div className="flex items-center justify-center gap-2 bg-red-50 border border-red-200 text-red-500 text-xs font-bold py-2.5 rounded-xl">
                        <i className="ri-close-circle-line text-base"></i>
                        تم إلغاء هذا الطلب
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}